import { useState, useRef, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import {
  Box,
  Stack,
  Typography,
  MenuItem,
  Select,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  Paper,
  Divider,
  Collapse,
} from '@mui/material';
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import ButtonLoading from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import { Formik, FormikProps } from 'formik';
import { TenantHomeDashboardNavigationBar } from 'components/LayoutComponent/VerticalLayout/HeaderBar';
import { PATH_CONSTANT } from 'constant/PathConstant';
import { useListCurrentSystemDropdown } from 'services/v1/TenantMaster/MasterTable/SystemTableDataService';
import {
  useCopyDataTenantList,
  useCopyDataSubjectList,
  useDeployTenant,
  useGetListOrganizationDropdown,
} from 'services/v1/TenantMaster/MasterTable/TenantTableDataService';
import { MEMBER_LIST_STATIC_RECORD } from 'data/MemberListRecord';
import {
  TenantDetails,
  CopyDataSubjectFormItem,
  DataInitOption,
} from 'types/api/TenantMaster/MasterTable/TenantTableDataTypes';
import { useUsersForDeployTenant } from 'services/v1/SystemTenant/TenantTableData';
import { useServerListDropdown } from 'services/v1/TenantMaster/MasterTable/ServerTableDataService';
import { CreateAndDeployTenantValidationSchema } from 'config/FormValidaton/TenantMaster/MasterTable/TenantTableValidationSchema';
import { ModalTenantDeploymentProgress } from './ModalTenantDeploymentProgress';

type ChipColor = 'success' | 'warning';

export const userSelectionDataGridStyle = {
  '& .MuiCheckbox-root svg': {
    backgroundColor: 'transparent',
    color: '#98A2AE',
    borderRadius: 2,
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#EDF2FF',
    borderTop: '1px solid #E3EBF6',
    borderBottom: '1px solid #E3EBF6',
    textAlign: 'center',
    display: 'flex',
  },
};

export const autocompleteStyles = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    padding: '1px 2px',
  },
  '& .MuiAutocomplete-popper': {
    fontSize: '10px',
  },
};

export const textInputAutoCompleteStyles = {
  '& .MuiOutlinedInput': {
    padding: '1px 2px',
  },
};

const chipStyle = {
  mr: 1,
  fontSize: '10px',
};

const optionStyles = { '& > span': { fontSize: '14px', mr: 2, flexShrink: 0 } };

const sectionHeaderStyles = {
  color: '#3B4797',
  fontWeight: 600,
  fontSize: 14,
};

const sectionDescriptionStyles = {
  color: '#6B7280',
  fontSize: 12,
  mt: 0.5,
};

const fieldLabelStyles = {
  fontSize: 13,
  fontWeight: 500,
  color: '#374151',
  minWidth: 140,
  mb: 0.5,
};

const sectionPaperStyles = {
  p: 2.5,
  border: '1px solid #E3EBF6',
  borderRadius: 2,
  boxShadow: 'none',
};

export const getRolesBySelectedTenantCode = (tenantCode?: string | undefined) => {
  if (!tenantCode) return undefined;
  if (/dev/.test(tenantCode.toLowerCase())) {
    return ['Tester', 'Manager', 'Admin'];
  }
  if (/dem/.test(tenantCode.toLowerCase())) {
    return ['Packager', 'Trainer', 'Manager', 'Admin', 'Support'];
  }
  return ['Manager', 'Support', 'Admin'];
};

function getChipColor(type: string, status: string): ChipColor {
  const colorsMapping: Record<string, Record<string, string>> = {
    codeStatus: {
      [MEMBER_LIST_STATIC_RECORD.CODE_STATUS.LOCKED]: 'success',
      [MEMBER_LIST_STATIC_RECORD.CODE_STATUS.CHANGING]: 'warning',
    },
    demoStatus: {
      [MEMBER_LIST_STATIC_RECORD.DEMO_STATUS.LOCKED]: 'success',
      [MEMBER_LIST_STATIC_RECORD.DEMO_STATUS.CHANGING]: 'warning',
    },
    fieldStatus: {
      [MEMBER_LIST_STATIC_RECORD.FIELD_STATUS.RELEASED]: 'success',
      [MEMBER_LIST_STATIC_RECORD.FIELD_STATUS.NOT_AVAILABLE]: 'warning',
    },
  };
  return colorsMapping[type][status] as ChipColor;
}

function getChiplabel(status: string) {
  const labelMapping = {
    [MEMBER_LIST_STATIC_RECORD.CODE_STATUS.LOCKED]: 'CS: Locked',
    [MEMBER_LIST_STATIC_RECORD.CODE_STATUS.CHANGING]: 'CS: Changing',
    [MEMBER_LIST_STATIC_RECORD.DEMO_STATUS.LOCKED]: 'DS: Locked',
    [MEMBER_LIST_STATIC_RECORD.DEMO_STATUS.CHANGING]: 'DS: Changing',
    [MEMBER_LIST_STATIC_RECORD.FIELD_STATUS.RELEASED]: 'FS: Released',
    [MEMBER_LIST_STATIC_RECORD.FIELD_STATUS.NOT_AVAILABLE]: 'FS: Not Available',
  };
  return labelMapping?.[status] || '';
}

const formInitialValues: TenantDetails = {
  serverId: null,
  systemId: null,
  organizationId: null,
  displayName: null,
  dataInitOption: 'empty',
  copyDataTenantId: null,
  copyDataSubjects: [],
  userId: [],
};

const chipRenderFieldOtions = ['codeStatus', 'demoStatus', 'fieldStatus'];

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <Box mb={2}>
      <Typography sx={sectionHeaderStyles}>{title}</Typography>
      <Typography sx={sectionDescriptionStyles}>{description}</Typography>
    </Box>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Stack spacing={0.5} sx={{ mt: 2 }}>
      <Typography sx={fieldLabelStyles}>
        {label}
        {required && <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>}
      </Typography>
      {children}
    </Stack>
  );
}

function StepIndicator({ step, total, active }: { step: number; total: number; active: boolean }) {
  return (
    <Stack direction='row' alignItems='center' spacing={1} mb={2}>
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: active ? '#3B4797' : '#E3EBF6',
          color: active ? '#fff' : '#9CA3AF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {step}
      </Box>
      <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>
        Step {step} of {total}
      </Typography>
    </Stack>
  );
}

export default function AddTenantPage() {
  const formikRef = useRef<FormikProps<TenantDetails>>(null);
  const navigate = useNavigate();
  const [selectedFormValue, setSelectedFormValue] = useState<TenantDetails>(formInitialValues);
  const [includeCustomerOrganizations, setIncludeCustomerOrganizations] = useState(false);
  const [deploymentTenantId, setDeploymentTenantId] = useState<string | null>(null);
  const [isDeploymentModalOpen, setIsDeploymentModalOpen] = useState(false);
  const { data: allSystemListDropdown, isLoading: isLoadingSystem } = useListCurrentSystemDropdown();
  const { data: organizationsDropdown, isLoading: isLoadingOrganization } = useGetListOrganizationDropdown(
    selectedFormValue.systemId?.value || undefined,
    includeCustomerOrganizations,
  );
  const { data: usersData, isLoading: isLoadingUsers } = useUsersForDeployTenant(
    getRolesBySelectedTenantCode(selectedFormValue.organizationId?.label),
    selectedFormValue.organizationId?.label,
  );
  const { data: copyDataOptions, isLoading: isLoadingCopyData } = useCopyDataTenantList(
    selectedFormValue.systemId?.value || undefined,
  );
  const { data: copyDataSubjects, isLoading: isLoadingSubjects } = useCopyDataSubjectList(
    selectedFormValue.copyDataTenantId?.id || undefined,
  );
  const { data: serverOptions, isLoading: isLoadingServer } = useServerListDropdown();
  const { mutate: deploySystem, isLoading: isDeploying, error, isError } = useDeployTenant();

  const isAnyLoading =
    isLoadingSystem || isLoadingOrganization || isLoadingServer || isLoadingUsers || isLoadingCopyData;

  const columns: GridColDef[] = [
    { field: 'fullName', headerName: 'Username', width: 180 },
    { field: 'roleType', headerName: 'Role Type', width: 80 },
    { field: 'roleName', headerName: 'Role Name', width: 170 },
    { field: 'userName', headerName: 'Email', width: 240 },
  ];

  const handleSubjectPolicyChange = useCallback(
    (subjectValue: string | number, policy: 'COPY_ALL' | 'COPY_SOME') => {
      const updated = (selectedFormValue.copyDataSubjects || []).map((s) =>
        s.value === subjectValue
          ? { ...s, copyDataPolicy: policy, recordLimit: policy === 'COPY_ALL' ? undefined : s.recordLimit }
          : s,
      );
      setSelectedFormValue((prev) => ({ ...prev, copyDataSubjects: updated }));
      formikRef.current?.setFieldValue('copyDataSubjects', updated);
    },
    [selectedFormValue.copyDataSubjects],
  );

  const handleSubjectRecordLimitChange = useCallback(
    (subjectValue: string | number, limit: string) => {
      const parsed = limit === '' ? undefined : parseInt(limit, 10);
      const updated = (selectedFormValue.copyDataSubjects || []).map((s) =>
        s.value === subjectValue ? { ...s, recordLimit: parsed } : s,
      );
      setSelectedFormValue((prev) => ({ ...prev, copyDataSubjects: updated }));
      formikRef.current?.setFieldValue('copyDataSubjects', updated);
    },
    [selectedFormValue.copyDataSubjects],
  );

  const subjectColumns: GridColDef[] = [
    { field: 'label', headerName: 'Subject Name', flex: 1, minWidth: 200 },
    {
      field: 'copyDataPolicy',
      headerName: 'Copy Data Policy',
      width: 160,
      renderCell: (params: GridRenderCellParams) => {
        const subject = (selectedFormValue.copyDataSubjects || []).find((s) => s.value === params.row.value);
        if (!subject) return null;
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Select
              value={subject.copyDataPolicy}
              onChange={(e) =>
                handleSubjectPolicyChange(params.row.value, e.target.value as 'COPY_ALL' | 'COPY_SOME')
              }
              size='small'
              sx={{ height: 28, fontSize: 12, width: 140 }}
            >
              <MenuItem value='COPY_ALL'>Copy All</MenuItem>
              <MenuItem value='COPY_SOME'>Copy Some</MenuItem>
            </Select>
          </div>
        );
      },
    },
    {
      field: 'recordLimit',
      headerName: 'Record Limit',
      width: 140,
      renderCell: (params: GridRenderCellParams) => {
        const subject = (selectedFormValue.copyDataSubjects || []).find((s) => s.value === params.row.value);
        if (!subject || subject.copyDataPolicy === 'COPY_ALL') return null;
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <TextField
              type='number'
              size='small'
              value={subject.recordLimit ?? ''}
              onChange={(e) => handleSubjectRecordLimitChange(params.row.value, e.target.value)}
              inputProps={{ min: 1 }}
              sx={{ width: 110, '& .MuiOutlinedInput-root': { height: 28, fontSize: 12 } }}
            />
          </div>
        );
      },
    },
  ];

  const selectedSubjectIds = useMemo(() => {
    return (selectedFormValue.copyDataSubjects || []).map((subject) => subject.value);
  }, [selectedFormValue.copyDataSubjects]);

  const selectedIds = useMemo(() => {
    if (selectedFormValue.userId) {
      return selectedFormValue.userId.map((user) => user.id);
    }
    return [];
  }, [selectedFormValue.userId]);

  const handleOnSave = (data: TenantDetails) => {
    const isCopyData = data.dataInitOption === 'copy';
    const payloadData = {
      systemId: data.systemId?.value || null,
      serverId: data.serverId?.value || null,
      organizationId: data.organizationId?.value || null,
      copyDataFromTenantId: isCopyData ? data.copyDataTenantId?.id || null : null,
      copyDataSubjects: isCopyData
        ? (data.copyDataSubjects || []).map((s) => ({
            subjectId: s.value,
            copyDataPolicy: s.copyDataPolicy,
            ...(s.copyDataPolicy === 'COPY_SOME' && s.recordLimit ? { recordLimit: s.recordLimit } : {}),
          }))
        : [],
      userId: data.userId,
      displayName: data.displayName,
    };
    deploySystem(payloadData, {
      onSuccess: (response) => {
        const { tenantId } = response.data;
        setDeploymentTenantId(tenantId);
        setIsDeploymentModalOpen(true);
        toast.success('Tenant deployment has been queued!');
      },
    });
  };

  const handleOnCancelInput = () => {
    navigate(PATH_CONSTANT.TENANT_MASTER.MASTER_TABLE.TENANT);
  };

  const handleCloseDeploymentModal = () => {
    setIsDeploymentModalOpen(false);
    setDeploymentTenantId(null);
    handleOnCancelInput();
  };

  return (
    <TenantHomeDashboardNavigationBar>
      <Box sx={{ height: '100%', backgroundColor: '#F8F9FC' }}>
        <Formik
          innerRef={formikRef}
          initialValues={formInitialValues}
          validationSchema={CreateAndDeployTenantValidationSchema}
          onSubmit={handleOnSave}
        >
          {(formikProps: FormikProps<TenantDetails>) => {
            const { values, setFieldValue, handleBlur, handleChange, touched, errors } = formikProps;
            return (
              <Box sx={{ maxWidth: 820, mx: 'auto', py: 3, px: 2 }}>
                {/* Page Header */}
                <Stack direction='row' alignItems='center' justifyContent='space-between' mb={3}>
                  <Box>
                    <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797', fontSize: 18 }}>
                      Create New Tenant
                    </Typography>
                    <Typography sx={{ color: '#6B7280', fontSize: 13, mt: 0.5 }}>
                      Set up a new tenant environment by completing the sections below. Fields marked with{' '}
                      <span style={{ color: '#EF4444' }}>*</span> are required.
                    </Typography>
                  </Box>
                  <Button
                    size='small'
                    onClick={handleOnCancelInput}
                    variant='main-table-panel-border'
                    disabled={isDeploying}
                  >
                    Cancel
                  </Button>
                </Stack>

                {/* Section 1: Tenant Configuration */}
                <Paper sx={sectionPaperStyles}>
                  <StepIndicator step={1} total={3} active={true} />
                  <SectionHeader
                    title='Tenant Configuration'
                    description='Define the system, organisation, and server for this tenant. These determine where the tenant will be deployed.'
                  />
                  <Divider sx={{ mb: 1 }} />

                  <FormField label='System' required>
                    <Autocomplete
                      options={allSystemListDropdown?.data || []}
                      // @ts-ignore
                      value={values.systemId}
                      getOptionLabel={(option) => option.label}
                      clearIcon={null}
                      contentEditable={false}
                      onChange={(event, value) => {
                        setIncludeCustomerOrganizations(false);
                        setSelectedFormValue((prevState) => ({
                          ...prevState,
                          organizationId: null,
                          serverId: null,
                          systemId: value,
                          copyDataTenantId: null,
                          copyDataSubjects: [],
                          userId: [],
                        }));
                        setFieldValue('systemId', value);
                        setFieldValue('organizationId', null);
                        setFieldValue('serverId', null);
                        setFieldValue('copyDataTenantId', null);
                        setFieldValue('copyDataSubjects', []);
                        setFieldValue('userId', []);
                      }}
                      renderOption={(props, option) => (
                        <Box component='li' sx={optionStyles} {...props}>
                          <span>{option.label}</span>
                          {chipRenderFieldOtions.map((field) => (
                            <Chip
                              // @ts-ignore
                              label={getChiplabel(option[field] || '')}
                              // @ts-ignore
                              color={getChipColor(field, option[field])}
                              variant='outlined'
                              size='small'
                              sx={chipStyle}
                            />
                          ))}
                        </Box>
                      )}
                      sx={autocompleteStyles}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          sx={textInputAutoCompleteStyles}
                          placeholder='Select the system this tenant belongs to'
                          size='small'
                        />
                      )}
                    />
                  </FormField>

                  {values.systemId && (
                    <Box sx={{ mt: 1.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size='small'
                            checked={includeCustomerOrganizations}
                            disabled={isDeploying}
                            sx={{ color: '#98A2AE' }}
                            inputProps={{ 'aria-describedby': 'direct-customer-deployment-description' }}
                            onChange={(event) => {
                              setIncludeCustomerOrganizations(event.target.checked);
                              setSelectedFormValue((prevState) => ({
                                ...prevState,
                                organizationId: null,
                                serverId: null,
                                userId: [],
                              }));
                              setFieldValue('organizationId', null);
                              setFieldValue('serverId', null);
                              setFieldValue('userId', []);
                            }}
                          />
                        }
                        label={
                          <Typography variant='input-label' fontSize={13}>
                            Deploy a real customer directly under this system
                          </Typography>
                        }
                      />
                      <Typography
                        id='direct-customer-deployment-description'
                        variant='input-label-gray'
                        component='p'
                        sx={{ fontSize: 12, ml: 3.5 }}
                      >
                        Include customer organisations even if this system has not been released.
                      </Typography>
                    </Box>
                  )}

                  <FormField label='Organisation' required>
                    <Autocomplete
                      options={organizationsDropdown?.data || []}
                      // @ts-ignore
                      value={values.organizationId}
                      disabled={isAnyLoading || !values.systemId}
                      getOptionLabel={(option) => option.label}
                      clearIcon={null}
                      contentEditable={false}
                      onChange={(event, value) => {
                        setSelectedFormValue((prevState) => ({
                          ...prevState,
                          organizationId: value,
                          userId: [],
                        }));
                        setFieldValue('organizationId', value);
                        setFieldValue('userId', []);
                      }}
                      renderOption={(props, option) => (
                        <Box component='li' sx={optionStyles} {...props}>
                          <span>{option.label}</span>
                        </Box>
                      )}
                      sx={autocompleteStyles}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          sx={textInputAutoCompleteStyles}
                          placeholder={isAnyLoading ? 'Loading...' : 'Select the organisation for this tenant'}
                          size='small'
                        />
                      )}
                    />
                  </FormField>

                  <FormField label='Server' required>
                    <Autocomplete
                      options={serverOptions || []}
                      // @ts-ignore
                      value={values.serverId}
                      disabled={isAnyLoading || !values.systemId || !values.organizationId}
                      getOptionLabel={(option) => option.label}
                      clearIcon={null}
                      contentEditable={false}
                      onChange={(event, value) => {
                        setFieldValue('serverId', value);
                      }}
                      renderOption={(props, option) => (
                        <Box component='li' sx={optionStyles} {...props}>
                          <span>{option.label}</span>
                        </Box>
                      )}
                      sx={autocompleteStyles}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          sx={textInputAutoCompleteStyles}
                          placeholder='Select the deployment server'
                          size='small'
                        />
                      )}
                    />
                  </FormField>

                  <FormField label='Display Name'>
                    <TextField
                      fullWidth
                      size='small'
                      placeholder='e.g. Teamwork — a friendly name for this tenant'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values?.displayName}
                      name='displayName'
                      error={touched.displayName && Boolean(errors.displayName)}
                      helperText={touched.displayName && errors.displayName}
                    />
                  </FormField>
                </Paper>

                {/* Section 2: Initial Data */}
                <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
                  <StepIndicator step={2} total={3} active={!!values.systemId} />
                  <SectionHeader
                    title='Initial Data'
                    description='Choose whether to start with an empty tenant or pre-populate it by copying data from an existing tenant in the same system.'
                  />
                  <Divider sx={{ mb: 1 }} />

                  <RadioGroup
                    value={values.dataInitOption || 'empty'}
                    onChange={(e) => {
                      const option = e.target.value as DataInitOption;
                      setFieldValue('dataInitOption', option);
                      setSelectedFormValue((prev) => ({
                        ...prev,
                        dataInitOption: option,
                        ...(option === 'empty' ? { copyDataTenantId: null, copyDataSubjects: [] } : {}),
                      }));
                      if (option === 'empty') {
                        setFieldValue('copyDataTenantId', null);
                        setFieldValue('copyDataSubjects', []);
                      }
                    }}
                    sx={{ mt: 1 }}
                  >
                    <Paper
                      variant='outlined'
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        borderColor: values.dataInitOption === 'empty' ? '#3B4797' : '#E3EBF6',
                        backgroundColor: values.dataInitOption === 'empty' ? '#F0F2FF' : '#fff',
                        borderRadius: 1.5,
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        setFieldValue('dataInitOption', 'empty');
                        setSelectedFormValue((prev) => ({
                          ...prev,
                          dataInitOption: 'empty',
                          copyDataTenantId: null,
                          copyDataSubjects: [],
                        }));
                        setFieldValue('copyDataTenantId', null);
                        setFieldValue('copyDataSubjects', []);
                      }}
                    >
                      <FormControlLabel
                        value='empty'
                        control={<Radio size='small' sx={{ '&.Mui-checked': { color: '#3B4797' } }} />}
                        label={
                          <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Start Empty</Typography>
                            <Typography sx={{ fontSize: 11, color: '#6B7280' }}>
                              Create a clean tenant with no pre-existing data. You can add data later.
                            </Typography>
                          </Box>
                        }
                        sx={{ m: 0, alignItems: 'flex-start' }}
                      />
                    </Paper>
                    <Paper
                      variant='outlined'
                      sx={{
                        p: 1.5,
                        borderColor: values.dataInitOption === 'copy' ? '#3B4797' : '#E3EBF6',
                        backgroundColor: values.dataInitOption === 'copy' ? '#F0F2FF' : '#fff',
                        borderRadius: 1.5,
                        cursor: values.systemId ? 'pointer' : 'default',
                        opacity: values.systemId ? 1 : 0.5,
                      }}
                      onClick={() => {
                        if (!values.systemId) return;
                        setFieldValue('dataInitOption', 'copy');
                        setSelectedFormValue((prev) => ({ ...prev, dataInitOption: 'copy' }));
                      }}
                    >
                      <FormControlLabel
                        value='copy'
                        control={<Radio size='small' sx={{ '&.Mui-checked': { color: '#3B4797' } }} />}
                        label={
                          <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Copy from Existing Tenant</Typography>
                            <Typography sx={{ fontSize: 11, color: '#6B7280' }}>
                              Pre-populate this tenant with subject data copied from another tenant in the selected
                              system.
                            </Typography>
                          </Box>
                        }
                        disabled={!values.systemId}
                        sx={{ m: 0, alignItems: 'flex-start' }}
                      />
                    </Paper>
                  </RadioGroup>

                  <Collapse in={values.dataInitOption === 'copy'}>
                    <Box sx={{ mt: 2, pl: 1, borderLeft: '3px solid #3B4797', ml: 1.5 }}>
                      <FormField label='Source Tenant'>
                        <Autocomplete
                          options={copyDataOptions?.data || []}
                          value={values.copyDataTenantId}
                          disabled={isAnyLoading || !values.systemId || !values.organizationId}
                          getOptionLabel={(option) => `${option.tenantName} (${option.organizationName})`}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          clearIcon={null}
                          onChange={(event, value) => {
                            setSelectedFormValue((prevState) => ({
                              ...prevState,
                              copyDataTenantId: value,
                              copyDataSubjects: [],
                            }));
                            setFieldValue('copyDataTenantId', value);
                            setFieldValue('copyDataSubjects', []);
                          }}
                          renderOption={(props, option) => (
                            <Box component='li' sx={optionStyles} {...props}>
                              <span>
                                {option.tenantName} ({option.organizationName})
                              </span>
                            </Box>
                          )}
                          sx={autocompleteStyles}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={textInputAutoCompleteStyles}
                              placeholder={isLoadingCopyData ? 'Loading...' : 'Select the tenant to copy data from'}
                              size='small'
                            />
                          )}
                        />
                      </FormField>

                      <Collapse in={!!values.copyDataTenantId}>
                        <Stack spacing={1} sx={{ mt: 2 }}>
                          <Typography sx={fieldLabelStyles}>Select Subjects to Copy</Typography>
                          <Typography sx={{ fontSize: 11, color: '#6B7280', mb: 1 }}>
                            Check the subjects you want to copy. For each subject, choose whether to copy all records or
                            limit the number of records copied.
                          </Typography>
                          <div style={{ height: 250, width: '100%' }}>
                            {!isLoadingSubjects && (
                              <DataGridPro
                                loading={isLoadingSubjects}
                                selectionModel={selectedSubjectIds}
                                sx={userSelectionDataGridStyle}
                                density='compact'
                                rows={copyDataSubjects?.data || []}
                                columns={subjectColumns}
                                getRowId={(row) => row.value}
                                onSelectionModelChange={(newSelection) => {
                                  if (copyDataSubjects?.data) {
                                    const existing = selectedFormValue.copyDataSubjects || [];
                                    const selectedItems: CopyDataSubjectFormItem[] = newSelection.map((id: any) => {
                                      const prev = existing.find((s) => s.value === id);
                                      if (prev) return prev;
                                      const item = copyDataSubjects.data.find((item) => item.value === id);
                                      return {
                                        value: item!.value,
                                        label: item!.label,
                                        copyDataPolicy: 'COPY_ALL' as const,
                                      };
                                    });
                                    setSelectedFormValue((prevState) => ({
                                      ...prevState,
                                      copyDataSubjects: selectedItems,
                                    }));
                                    setFieldValue('copyDataSubjects', selectedItems);
                                  }
                                }}
                                pagination={false}
                                hideFooter
                                checkboxSelection
                              />
                            )}
                          </div>
                        </Stack>
                      </Collapse>
                    </Box>
                  </Collapse>
                </Paper>

                {/* Section 3: User Assignment */}
                <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
                  <StepIndicator step={3} total={3} active={!!values.systemId && !!values.organizationId} />
                  <SectionHeader
                    title='User Assignment'
                    description='Select the users who will have access to this tenant. Users are filtered based on the selected organisation and their roles.'
                  />
                  <Divider sx={{ mb: 2 }} />

                  <div style={{ height: 280, width: '100%' }}>
                    {!isLoadingUsers && (
                      <DataGridPro
                        loading={isLoadingUsers}
                        selectionModel={selectedIds}
                        sx={userSelectionDataGridStyle}
                        density='compact'
                        rows={usersData?.data || []}
                        columns={columns}
                        onSelectionModelChange={(newSelection) => {
                          if (usersData?.data) {
                            const newUserIdValues = newSelection.map((id: any) => {
                              return usersData.data.find((user) => user.id === id);
                            });
                            // @ts-ignore
                            setSelectedFormValue((prevState) => ({
                              ...prevState,
                              userId: newUserIdValues,
                            }));
                            setFieldValue('userId', newUserIdValues);
                          }
                        }}
                        pagination={false}
                        hideFooter
                        rowsPerPageOptions={[5]}
                        checkboxSelection={
                          !isAnyLoading || (!!values.systemId && !!values.organizationId && !!values.serverId)
                        }
                      />
                    )}
                  </div>
                  {selectedIds.length > 0 && (
                    <Typography sx={{ fontSize: 12, color: '#3B4797', mt: 1, fontWeight: 500 }}>
                      {selectedIds.length} user{selectedIds.length > 1 ? 's' : ''} selected
                    </Typography>
                  )}
                </Paper>

                {/* Error Alert */}
                {isError && (
                  <Alert severity='error' sx={{ mt: 2.5, borderRadius: 2 }}>
                    {error.response?.data.message}
                  </Alert>
                )}

                {/* Action Buttons */}
                <Stack direction='row' spacing={2} justifyContent='flex-end' sx={{ mt: 3 }}>
                  <Button disabled={isDeploying} onClick={handleOnCancelInput} variant='main-table-panel-border'>
                    Cancel
                  </Button>
                  <ButtonLoading
                    loading={isDeploying}
                    variant='main-table-panel'
                    onClick={() => formikProps.handleSubmit()}
                    disabled={!CreateAndDeployTenantValidationSchema?.isValidSync(formikProps.values) || isDeploying}
                  >
                    Create & Deploy Tenant
                  </ButtonLoading>
                </Stack>
              </Box>
            );
          }}
        </Formik>
      </Box>
      <ModalTenantDeploymentProgress
        visible={isDeploymentModalOpen}
        tenantId={deploymentTenantId}
        onClose={handleCloseDeploymentModal}
      />
    </TenantHomeDashboardNavigationBar>
  );
}
